import React, { useMemo, useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  Button
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import Flatpickr from "react-flatpickr";
import TableContainerV1 from "components/Common/TableContainerV1"
import moment from "moment"
import { isEmpty } from "lodash"
import ViewInquiry from "./ViewInquiry"

import { getInquiryList, getProductInquiryCount } from "helpers/UAM/inquiry.helper"
import { getNonRegisterDealer } from "../../helpers/UAM/inquiry.helper"

const Inquiry = () => {
  document.title = "Product Inquiry List | DMS "

  const navigate = useNavigate();
  const [inquiryCount, setInquiryCount] = useState({})
  const [inquiryList, setInquiryList] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  });
  const [inquiryInfo, setInquiryInfo] = useState({});
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action", "dealer"],
  });

  const [customActiveTab, setCustomActiveTab] = useState("1")
  const flatpickrRef = useRef(null);

  const { roleName } = JSON.parse(localStorage.getItem("user"))

  const toggleCustom = tab => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab)
    }
  }

  const getInquiry = async (bodyData) => {
    let findInquiryList = customActiveTab === '1' ? getInquiryList : getNonRegisterDealer
    const response = await findInquiryList(bodyData);
    if (response.status) {
      setInquiryList(response.data.data);
      const { totalItems, totalPages, currentPage } = response.data
      setPagination({
        pageIndex: currentPage,
        pageCount: totalPages,
        totalRecords: totalItems,
      })
      if(customActiveTab === '1'){
        setInquiryCount({dealer:totalItems, 'non-dealer': inquiryCount['non-dealer']})
      }
    } else {
      setInquiryList([]);
      setPagination({
        pageIndex: 0,
        pageCount: 0,
        totalRecords: 0,
      })
    }
  }

  useEffect(() => {
    getProductInquiryCount()
      .then(responseFromApi => {
        let inqCount = {}
        for (let item of responseFromApi.data) {
          inqCount[item.inquiryType] = item.count
        }
        setInquiryCount(inqCount)
      })
      .catch((error) => {
        console.error(error);
      })
  }, [])

  useEffect(() => {
    const bodyData = {
      page: currentPage,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
      filterType: filters
    }
    getInquiry(bodyData);
  }, [searchValue, pageSize, columnId, sortingOrderStatus, currentPage]);

  useEffect(() => {
    setSearchValue("");
    setFilters({
      startDate: '',
      endDate: '',
      status: ''
    })
    flatpickrRef.current.flatpickr.clear()
    setPageSize(10)
  }, [customActiveTab])

  const handlePageChange = (page, pageSize) => {
    setPageSize(pageSize);
    setCurrentPage(page);
  }

  const handleSortColumn = (option) => {
    if (option.column == "dealer") return
    setSortingOptions(option);
    const columnName = option.column;
    setColumnId(columnName);
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder);
  };

  const handleSearchFilter = (value) => {
    setSearchValue(value);
    setCurrentPage(1)
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setFilters({
      startDate: '',
      endDate: '',
      status: ''
    })
    flatpickrRef.current.flatpickr.clear()
    setCurrentPage(1);
    let bodyData = {
      filterType: {
        startDate: '',
        endDate: '',
        status: ''
      },
      page: 1,
      searchKey: "",
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }

    getInquiry(bodyData)

  };

  const handleAuditLogs = (data) => {
    setInquiryInfo(data)
  }

  const ProductDescription = ({ value }) => {
    if (value && value.length > 50) {
      let newValue = value.substring(0, 50) + '...';
      return (
        <span className="d-inline" tabIndex="0" data-toggle="tooltip" title={value} dir="top" data-placement="top">
          {newValue}
        </span>
      )
    }
    return (
      <span>{value}</span>
    )
  }

  const columns = useMemo(
    () => [
      {
        Header: "Inquiry Number",
        accessor: "inquiryNumber",
        Cell: cellProps => {
          return cellProps.value ? `INQ-${cellProps.value}` : ""
        },
      },
      {
        Header: "Sub Inquiry Number",
        accessor: "subinquiryNumber",
        Cell: cellProps => {
          return cellProps.value ? `SUBINQ-${cellProps.value}` : ""
        },
      },
      {
        Header: "Dealer Name",
        accessor: "dealer",
        Cell: cellProps => {
          return (cellProps.value?.entityName ? cellProps.value?.entityName : "")
        },
      },
      {
        Header: "Product Description",
        accessor: "productDescription",
        width: '100px',
        filterable: true,
        Cell: cellProps => {
          return <ProductDescription value={cellProps.value} />
        },
      },
      {
        Header: "Inquiry Date",
        accessor: "createdAt",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? moment(cellProps.value).format("MMM DD, YYYY HH:mm A") : ""
        },
      },
      {
        Header: "Status",
        accessor: "productStatus",
        filterable: true,
        Cell: (cellProps) => cellProps.value ? (<span className="text-capitalize">{cellProps.value}</span>) : ""
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  onClick={() => {
                    const edit = cellProps.row.original
                    handleEdit(edit)
                  }}
                >
                  <i
                    className="mdi mdi-pencil font-size-16 text-success me-1"
                    id="edittooltip"
                  ></i>
                  Edit
                  <UncontrolledTooltip placement="top" target="edittooltip">
                    Edit
                  </UncontrolledTooltip>
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    handleAuditLogs(cellProps.row.original)
                  }}
                >
                  <i
                    className="bx bx-log-in-circle font-size-16 text-success me-1"
                    id="viewtooltip"
                  ></i>
                  Audit Logs
                  <UncontrolledTooltip placement="top" target="viewtooltip">
                    Audit Logs
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )
        },
      },
    ],
    []
  )

  // Non Register Inquiry
  const nonRegisterDealerColums = useMemo(
    () => [
      {
        Header: "Inquiry Number",
        accessor: "inquiryNumber",
        Cell: cellProps => {
          return cellProps.value ? `INQ-${cellProps.value}` : ""
        },
      },
      {
        Header: "Person Name",
        accessor: "fullName",
        Cell: cellProps => {
          return (cellProps.value ? cellProps.value : "-")
        },
      },
      {
        Header: "Product Description",
        accessor: "productDescription",
        filterable: true,
        Cell: cellProps => {
          let value = cellProps.value ? String(cellProps.value).substring(0, 50) : ""
          if (cellProps.value && cellProps.value.length > 50) {
            value = value + "..."
          }
          return value
        },
      },

      {
        Header: "Expected Quantity",
        accessor: "quantity",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? cellProps.value : ""
        },
      },
      {
        Header: "Inquiry Date",
        accessor: "createdAt",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? moment(cellProps.value).format("MMM DD, YYYY HH:mm A") : ""
        },
      },
      {
        Header: "Status",
        accessor: "productStatus",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? (<span className="text-capitalize">{cellProps.value}</span>) : ""
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  onClick={() => {
                    const rowdata = cellProps.row.original
                    handleNonDealerEdit(rowdata.id)
                  }}
                >
                  <i
                    className="mdi mdi-pencil font-size-16 text-success me-1"
                    id="edittooltip"
                  ></i>
                  Edit
                  <UncontrolledTooltip placement="top" target="edittooltip">
                    Edit
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )
        },
      },
    ]
  )


  const handleEdit = arg => {
    const subInquiryNumber = arg.subinquiryNumber
    const enCodedId = btoa(subInquiryNumber)
    navigate(`/${roleName}/inquiry/edit/${enCodedId}`);
  }

  const handleNonDealerEdit = id => {
    const enCodedId = btoa(id)
    navigate(`/${roleName}/user-inquiry/edit/${enCodedId}`);
  }

  const handleStatusChange = (value) => {
    setFilters({
      ...filters,
      status: value,
    })
    const bodyData = {
      filterType: {
        ...filters,
        status: value,
      },
      page: currentPage,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    getInquiry(bodyData)
  }

  const handleDateFilter = () => {
    const bodyData = {
      filterType: filters,
      page: currentPage,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    getInquiry(bodyData)
  }

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setFilters({
        ...filters,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
      })
    }
  };

  const customeFilterSection = (
    <div className="d-flex align-items-center justify-content-start">
      <div className="text-sm-start">
        <select className="form-select  me-3 show-dropdown" style={{ width: "150px" }}
          value={filters.status}
          onChange={(e) => {
            const value = e.target.value;
            handleStatusChange(value)
          }}
        >
          <option selected={filters.isActive == ""} value={""}>All</option>
          <option selected={filters.status == 'pending'} value={'pending'}>Pending</option>
          <option selected={filters.status == 'win'} value={'win'}>Closed (Win)</option>
          <option selected={filters.status == 'lost'} value={'lost'}>Closed (Lost)</option>
        </select>
      </div>
      <InputGroup>
        <Flatpickr
          ref={flatpickrRef}
          className="form-control"
          placeholder="Start Date - End Date"
          options={{
            mode: 'range',
            dateFormat: 'd-m-Y'
          }}
          value={[filters?.startDate, filters?.endDate]}
          onChange={handleDateChange}
        />
        <Button variant="primary"
          onClick={handleDateFilter}
          style={{ backgroundColor: "#344fa0", border: "none" }}>
          Go
        </Button>
      </InputGroup>
    </div>
  )

  const customeNonDealerFilterSection = (
    <div className="d-flex align-items-center justify-content-start">
      <InputGroup style={{ minWidth: '250px' }}>
        <Flatpickr
          ref={flatpickrRef}
          className="form-control"
          placeholder="Start Date - End Date"
          options={{
            mode: 'range',
            dateFormat: 'd-m-Y'
          }}
          value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
          onChange={handleDateChange}
        />
        <Button variant="primary"
          onClick={handleDateFilter}
          style={{ backgroundColor: "#344fa0", border: "none" }}>
          Go
        </Button>
      </InputGroup>
    </div>
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Product Inquiry" breadcrumbItem="Product Inquiry List" />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <Nav tabs className="nav-tabs-custom nav-justified mb-4 border-bottom border-1 border-gray-400">
                  <NavItem>
                    <NavLink
                      role="button"
                      className={
                        customActiveTab === "1"
                          ? 'bg-gray-100 text-success fw-bolder border-bottom border-2 border-success'
                          : 'text-7B8788'
                      }
                      onClick={() => {
                        toggleCustom("1")
                        flatpickrRef.current.flatpickr.clear()
                      }}
                    >
                      <span className="d-block d-sm-none">
                        <i className="fas fa-home"></i>
                      </span>
                      <span className="d-none d-sm-block">
                        Registered Dealer Inquiry({inquiryCount.dealer || '0'})
                      </span>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      role="button"
                      className={
                        customActiveTab === "2"
                          ? 'bg-gray-100 text-success fw-bolder border-bottom border-2 border-success'
                          : 'text-7B8788'
                      }
                      onClick={() => {
                        toggleCustom("2")
                        flatpickrRef.current.flatpickr.clear()
                      }}
                    >
                      <span className="d-block d-sm-none">
                        <i className="far fa-user"></i>
                      </span>
                      <span className="d-none d-sm-block">
                        Non Registered Dealer Inquiry({inquiryCount['non-dealer'] || '0'})
                      </span>
                    </NavLink>
                  </NavItem>

                </Nav>

                {/* Register Inquiry */}
                <TabContent
                  activeTab={customActiveTab}
                  className="p-3 text-muted"
                >
                  <TabPane tabId="1">
                    <Row>
                      <Col>
                        <TableContainerV1
                          columns={columns}
                          data={inquiryList}
                          pagination={pagination}
                          handlePageChange={handlePageChange}
                          handleSearchFilter={handleSearchFilter}
                          searchHelperText={customActiveTab == "1" ? "Search by Inquiry Number, Sub Inquiry Number, Description, Inquiry Date" : "Search by Inquiry Number, Person Name, Description"}
                          // customSection={buttonSection}
                          customFilterSection={customeFilterSection}
                          sortOptions={sortingOptions}
                          handleSortColumn={handleSortColumn}
                          handleClearSearch={handleClearSearch}
                          className="custom-header-css"
                          isTab={customActiveTab === "1" ? true : false}
                        />
                      </Col>
                    </Row>

                    <ViewInquiry show={!isEmpty(inquiryInfo)} handleClose={() => setInquiryInfo({})} inquiryInfo={inquiryInfo} />
                  </TabPane>

                  {/* Non Register Inquiry */}
                  <TabPane tabId="2">
                    <Row>
                      <Col>
                        <TableContainerV1
                          columns={nonRegisterDealerColums}
                          data={inquiryList}
                          pagination={pagination}
                          handlePageChange={handlePageChange}
                          customFilterSection={customeNonDealerFilterSection}
                          handleSearchFilter={handleSearchFilter}
                          sortOptions={sortingOptions}
                          handleSortColumn={handleSortColumn}
                          handleClearSearch={handleClearSearch}
                          className="custom-header-css"
                          isTab={customActiveTab === "2" ? true : false}
                        />
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Inquiry